import React, { useState, useMemo } from 'react';

import { Grid, Typography, Card, CardContent, Button, IconButton } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LockIcon from '@mui/icons-material/Lock';

import { InfoTooltip } from '../../HOC/Tooltips';
import { COLOR_E, COLOR_TOOLTIP_BACKGROUND, exchangeRates } from 'src/const';
import {
  getVRMs,
  getSupplyByPropertySize,
  getSupplyByPropertyType,
  getSupplyByChannel,
  getHeadingStyle,
  getPreviewTextObj,
  getSupplyByPropertyRevenue,
  convertCurrency,
  formatCurrency,
} from 'src/utils/functions';
import { usePreview } from '../../../utils/hooks';
import { GraphPreview } from 'src/App/Components';
import { ViewDetailsButton } from 'src/App/Components/Buttons';
import { DynamicPieChart, DynamicBarChart } from '../../Components/Charts';
import { Host, BarGraphData } from 'src/interfaces/idashboard';
import { SupplyCardPropTypes } from 'src/interfaces/iproptypes';
import { infoIconStyle, cardTitle, infoIconButton } from 'src/App/Dashboards/Market/styles/style';
import {
  COLOR_PRIMARY_1,
  COLOR_PRIMARY_1_600,
  COLOR_SECONDARY_1_600,
  COLOR_SECONDARY_1_300,
  COLOR_SECONDARY_2_600,
  COLOR_TEXT_2,
  COLOR_PRIMARY_2,
  COLOR_TERTIARY_1_400,
  COLOR_TERTIARY_1_200,
  COLOR_TERTIARY_1_500,
  COLOR_TERTIARY_1_300,
  COLOR_PRIMARY_1_700,
  COLOR_PRIMARY_1_500,
  COLOR_PRIMARY_1_400,
  COLOR_TERTIARY_1_100,
  COLOR_PRIMARY_1_800,
  COLOR_SECONDARY_2,
  COLOR_TEXT_3,
} from 'src/colors';
import { dynamicDataAnimationZindex } from 'src/zIndex';
import { useSelector } from 'react-redux';
import BasicTable from 'src/App/Components/Tables/BasicTable';

const SupplyCard = ({
  listings,
  otaListings,
  handleOnSampleClick,
  isDisabled,
  setOpenTop10ManagerModal,
  filterVal,
  currency,
}: SupplyCardPropTypes) => {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const annualRevenue = useSelector(
    state => (state as any).dashboard.maxSampleRevPotentialPropertySize
  );

  const propertySizePreviewTextObj = getPreviewTextObj(
    'supply',
    'propertySize',
    filterVal,
    listings.data
  );
  const propertyTypePreviewTextObj = getPreviewTextObj(
    'supply',
    'propertyType',
    filterVal,
    listings.data
  );
  const channelPreviewTextObj = getPreviewTextObj(
    'supply',
    'channel',
    filterVal,
    listings.data,
    otaListings.data
  );

  const supplyForDummmyPropertyRevenue = val => {
    return [
      {
        keyShort: 'Studio',
        keyLong: 'Studio',
        value: 0.25 * val,
      },
      {
        keyShort: '1',
        keyLong: '1 bedroom',
        value: 0.48 * val,
      },
      {
        keyShort: '2',
        keyLong: '2 bedroom',
        value: 0.73 * val,
      },
      {
        keyShort: '3',
        keyLong: '3 bedroom',
        value: 0.56 * val,
      },
      {
        keyShort: '4',
        keyLong: '4 bedroom',
        value: 0.69 * val,
      },
      {
        keyShort: '5',
        keyLong: '5 bedroom',
        value: 0.4 * val,
      },
      {
        keyShort: '6+',
        keyLong: '6+ bedroom',
        value: 0.6 * val,
      },
      {
        keyShort: 'rooms',
        keyLong: 'private/hotel/shared rooms',
        value: 0.4 * val,
      },
    ];
  };

  let top5HostsSupply: (string | number)[][] = [];
  if (!listings.loading) {
    const top5Hosts: Host[] = getVRMs(
      listings.data,
      'totalNumberOfListings',
      isDisabled
    ).top10ManagerArray.slice(0, 5);
    top5HostsSupply = top5Hosts.map(host => [
      host.name,
      host.totalNumberOfListings,
      '★' + Number(host.totalRating / host.totalNumberOfListings).toFixed(2),
      isDisabled
        ? Math.floor(Math.random() * 28950)
        : exchangeRates[currency].symbol +
          formatCurrency(convertCurrency(currency, host.revenue) + ''),
    ]);
  }
  const supplyByPropertyRevenue = useMemo(() => {
    return isDisabled
      ? supplyForDummmyPropertyRevenue(annualRevenue * 24)
      : getSupplyByPropertyRevenue(listings.data, currency);
  }, [listings, currency]);

  const supplyByPropertySize = useMemo(() => {
    return getSupplyByPropertySize(listings.data);
  }, [listings]);

  const supplyByChannel = useMemo(() => {
    return getSupplyByChannel(listings.data, otaListings.data);
    // return getSupplyByChannel(listings.data, []);
  }, [listings, otaListings]);

  const supplyByPropertyType = useMemo(() => {
    return getSupplyByPropertyType(listings.data);
  }, [listings]);

  const top3HostsSupply: BarGraphData[] = useMemo(() => {
    if (!listings.loading) {
      const top3Hosts: Host[] = getVRMs(
        listings.data,
        'totalNumberOfListings',
        isDisabled
      ).top10ManagerArray.slice(0, 3);
      return top3Hosts.map(host => ({
        keyLong: host.name,
        value: host.totalNumberOfListings,
      }));
    }
    return [];
  }, [listings]);

  const [isPropertySizePreview, handlePropertySizePreviewMode] = usePreview() as [
    boolean,
    () => void
  ];
  const [
    isPropertyTypeAndChannelPreview,
    handlePropertyTypeAndChannelPreviewMode,
  ] = usePreview() as [boolean, () => void];

  const licensePercentage = useMemo(() => {
    let count = 0;
    listings.data.forEach(listing => {
      if (listing.hasLicense) {
        count++;
      }
    });
    return Math.floor((count / listings.data.length) * 100);
  }, [listings]);

  return (
    <>
      <Card>
        <CardContent>
          <Grid
            container
            direction="column"
            // justifyContent="space-between"
            // alignItems="flex-start"
            item
            xs={12}
          >
            {/* <Typography variant="h2" sx={cardTitle}>
              Supply Distribution
              <ClickAwayListener onClickAway={() => setIsTooltipOpen(false)}>
                <InfoTooltip
                  title={
                    <Card>
                      <CardContent sx={{ background: COLOR_TOOLTIP_BACKGROUND }}>
                        <Grid container direction="column" alignItems="flex-start">
                          <Typography variant="h6" sx={{ fontWeight: 'bolder' }}>
                            Supply Distribution
                          </Typography>
                          <br />
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            The double pie chart and the segmented bar represents the breakdown of
                            total active listings in the market by:
                          </Typography>
                          <br />
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            <span style={{ fontWeight: 900, textDecoration: 'underline' }}>
                              {'Channel (the inner pie): '}
                            </span>{' '}
                            Airbnb, VRBO, Booking.com, Tripadvisor
                          </Typography>
                          <br />
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            <span style={{ fontWeight: 900, textDecoration: 'underline' }}>
                              {'Property Type (the outer pie) (only Airbnb listings): '}
                            </span>{' '}
                            Entire Home/Apartment, Condo, Shared Room, Hotel Room, Private Room
                          </Typography>
                          <br />
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            <span style={{ fontWeight: 900, textDecoration: 'underline' }}>
                              {'Property Size (only Airbnb listings): '}
                            </span>{' '}
                            Studio, 1 to 6+ bedrooms
                          </Typography>
                          <br />
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            The <span style={{ fontWeight: 900 }}>Top contributors</span> to supply
                            lists the top VRM players in the market.
                          </Typography>
                        </Grid>
                      </CardContent>
                    </Card>
                  }
                  enterTouchDelay={0}
                  leaveTouchDelay={6000}
                  open={isTooltipOpen}
                  onOpen={() => setIsTooltipOpen(true)}
                  onClose={() => setIsTooltipOpen(false)}
                >
                  <IconButton
                    style={infoIconButton}
                    onClick={() => setIsTooltipOpen(prev => !prev)}
                  >
                    <InfoOutlinedIcon style={infoIconStyle} />
                  </IconButton>
                </InfoTooltip>
              </ClickAwayListener>
            </Typography> */}

            <Grid
              container
              direction="column"
              justifyContent="center"
              item
              xs={12}
              spacing={2}
              sx={{ marginTop: '5px' }}
            >
              <Grid container item id="propertySizeGraph">
                <Grid item xs={12}>
                  <Typography variant="body1" sx={getHeadingStyle(COLOR_TEXT_2)}>
                    Revenue Potential By Property Size
                    <InfoTooltip
                      title={
                        <Card>
                          <CardContent style={{ background: COLOR_TOOLTIP_BACKGROUND }}>
                            <Grid container direction="column" alignItems="flex-start">
                              <Typography variant="body2" style={{ fontWeight: 500 }}>
                                The average annual revenue potential for each different property
                                size in this market.
                              </Typography>
                            </Grid>
                          </CardContent>
                        </Card>
                      }
                      enterTouchDelay={0}
                      leaveTouchDelay={10000}
                    >
                      <IconButton style={infoIconButton}>
                        <InfoOutlinedIcon style={infoIconStyle} />
                      </IconButton>
                    </InfoTooltip>
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <div style={{ position: 'relative' }}>
                    <div
                      style={{
                        // filter: `blur(${isDisabled ? '6px' : '0px'})`,
                        pointerEvents: isDisabled ? 'none' : 'auto',
                      }}
                    >
                      <DynamicBarChart
                        layout="horizontal"
                        data={supplyByPropertyRevenue}
                        label="Bedrooms"
                        height={150}
                        unit={exchangeRates[currency].symbol}
                        barColor={COLOR_PRIMARY_1_600}
                        barAccentColor={COLOR_PRIMARY_1}
                        useShortHand={true}
                        isCurrency={true}
                      />
                    </div>
                    {isDisabled && (
                      <div
                        style={{
                          height: '81%',
                          position: 'absolute',
                          top: '0',
                          left: '27px',
                          width: '93%',
                          background: '#ffffff',
                          filter: 'blur( 6px)',
                          opacity: 0.8,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      ></div>
                    )}
                    {isDisabled && (
                      <Typography
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          fontSize: '26px',
                          fontWeight: 900,
                          color: COLOR_E,
                          transform: 'translate(-50%, -50%)',
                          cursor: 'pointer',
                        }}
                        onClick={() => {
                          handleOnSampleClick();
                        }}
                      >
                        PREMIUM
                      </Typography>
                    )}
                  </div>
                </Grid>
              </Grid>

              <Grid container item sx={{ width: '100%' }}>
                <Typography variant="body1" sx={getHeadingStyle(COLOR_TEXT_2)}>
                  Property Size
                  <InfoTooltip
                    enterTouchDelay={0}
                    leaveTouchDelay={30000}
                    title={
                      <Card>
                        <CardContent style={{ background: COLOR_TOOLTIP_BACKGROUND }}>
                          <Typography variant="body2" style={{ fontWeight: 500 }}>
                            This data only includes entire place listings on Airbnb.
                          </Typography>
                        </CardContent>
                      </Card>
                    }
                    style={{ cursor: 'pointer' }}
                  >
                    <IconButton
                      style={infoIconButton}
                      onClick={() => setIsTooltipOpen(prev => !prev)}
                    >
                      <InfoOutlinedIcon style={infoIconStyle} />
                    </IconButton>
                  </InfoTooltip>
                </Typography>
                {isPropertySizePreview ? (
                  <GraphPreview
                    parent="supply"
                    variant="propertySize"
                    useFriendlyDataName={propertySizePreviewTextObj.userFriendlyDataName}
                    previewVal={propertySizePreviewTextObj.previewVal}
                  />
                ) : (
                  <DynamicBarChart
                    layout="horizontal"
                    data={supplyByPropertySize}
                    unit="listings"
                    label="Bedrooms"
                    height={150}
                    barColor={COLOR_SECONDARY_2_600}
                    barAccentColor={COLOR_SECONDARY_2}
                    useShortHand={true}
                    isPropertySizeGraph ={true}
                  />
                )}
                <ViewDetailsButton
                  color={COLOR_PRIMARY_2}
                  isPreview={isPropertySizePreview}
                  handlePreviewMode={handlePropertySizePreviewMode}
                />
              </Grid>

              {isPropertyTypeAndChannelPreview ? (
                <Grid container item sx={{ width: '100%' }}>
                  <Grid item>
                    <Typography variant="body1" sx={getHeadingStyle(COLOR_TEXT_2)}>
                      Property Type
                    </Typography>
                    <GraphPreview
                      parent="supply"
                      variant="propertyType"
                      useFriendlyDataName={propertyTypePreviewTextObj.userFriendlyDataName}
                      previewVal={propertyTypePreviewTextObj.previewVal}
                    />
                  </Grid>
                  <Grid item>
                    <Typography variant="body1" sx={getHeadingStyle(COLOR_TEXT_2)}>
                      Channel Type
                    </Typography>
                    <GraphPreview
                      parent="supply"
                      variant="channel"
                      useFriendlyDataName={channelPreviewTextObj.userFriendlyDataName}
                      previewVal={channelPreviewTextObj.previewVal}
                    />
                  </Grid>
                </Grid>
              ) : (
                <Grid item container sx={{ width: '100%' }}>
                  <Typography variant="body1" sx={getHeadingStyle(COLOR_TEXT_2)}>
                    Property Type and Channel
                    <InfoTooltip
                      title={
                        <Card>
                          <CardContent style={{ background: COLOR_TOOLTIP_BACKGROUND }}>
                            <Grid container direction="column" alignItems="flex-start">
                              <Typography variant="body2" style={{ fontWeight: 500 }}>
                                Property Type and STR platform distribution
                              </Typography>
                            </Grid>
                          </CardContent>
                        </Card>
                      }
                      enterTouchDelay={0}
                      leaveTouchDelay={10000}
                    >
                      <IconButton style={infoIconButton}>
                        <InfoOutlinedIcon style={infoIconStyle} />
                      </IconButton>
                    </InfoTooltip>
                  </Typography>

                  <Grid
                    xs={12}
                    item
                    container
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Grid item xs={6}>
                      <DynamicPieChart
                        data={supplyByPropertyType}
                        unit={'listings'}
                        pieColors={[
                          COLOR_PRIMARY_1_800,
                          COLOR_PRIMARY_1_700,
                          COLOR_PRIMARY_1_600,
                          COLOR_PRIMARY_1_500,
                          COLOR_PRIMARY_1_400,
                        ]}
                        pieAccentColor={COLOR_PRIMARY_1}
                        label={'Property Type'}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <DynamicPieChart
                        data={supplyByChannel}
                        unit={'listings'}
                        pieColors={[
                          COLOR_TERTIARY_1_400,
                          COLOR_TERTIARY_1_300,
                          COLOR_TERTIARY_1_200,
                          COLOR_TERTIARY_1_100,
                        ]}
                        pieAccentColor={COLOR_TERTIARY_1_500}
                        label={'Channel'}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              )}
              <Grid item>
                <ViewDetailsButton
                  color={COLOR_PRIMARY_2}
                  isPreview={isPropertyTypeAndChannelPreview}
                  handlePreviewMode={handlePropertyTypeAndChannelPreviewMode}
                />
              </Grid>

              <Grid container item sx={{ width: '100%' }}>
                <Grid item xs={12}>
                  <Typography variant="body1" sx={getHeadingStyle(COLOR_TEXT_2)}>
                    Top {top5HostsSupply.length} Hosts
                    <InfoTooltip
                      title={
                        <Card>
                          <CardContent style={{ background: COLOR_TOOLTIP_BACKGROUND }}>
                            <Grid container direction="column" alignItems="flex-start">
                              <Typography variant="body2" style={{ fontWeight: 500 }}>
                                Top performing hosts based on total revenue
                              </Typography>
                            </Grid>
                          </CardContent>
                        </Card>
                      }
                      enterTouchDelay={0}
                      leaveTouchDelay={10000}
                    >
                      <IconButton style={infoIconButton}>
                        <InfoOutlinedIcon style={infoIconStyle} />
                      </IconButton>
                    </InfoTooltip>
                  </Typography>
                </Grid>
                <Grid item xs={12} sx={{ overflow: 'auto' }}>
                  <BasicTable
                    header={['Name', 'Listings', 'Rating', 'Revenue']}
                    rows={top5HostsSupply}
                    blurredColumns={isDisabled ? [3] : []}
                    onButtonClick={handleOnSampleClick}
                  />
                </Grid>
              </Grid>

              <Grid item sx={{ width: '100%' }}>
                <Typography variant="body2" style={{ alignItems: 'center' }}>
                  ** {isDisabled ? <LockIcon sx={{ color: COLOR_PRIMARY_1 }} /> : licensePercentage}
                  % of the listings are allowed to operate short-term rentals with a local license.
                </Typography>
              </Grid>

              <Grid
                container
                direction="row"
                justifyContent="space-between"
                item
                xs={12}
                sx={{ marginTop: '10px' }}
              >
                <Button onClick={() => setOpenTop10ManagerModal(true)}>View Top VRMs</Button>
              </Grid>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  );
};

export default React.memo(SupplyCard);
